import { useEffect } from "react"

/**
 * Polyfills `interactive-widget=resizes-content` for iOS Safari, which ignores
 * the viewport meta tag value. On Android Chrome and Firefox (which support
 * the meta tag natively), this hook detects zero offset and applies no overrides.
 *
 * When the virtual keyboard is open on iOS Safari, this hook:
 *   1. Fixes #app_container with its top pinned to the layout viewport
 *      (`top: 0`), its height extended to the visible bottom
 *      (`offsetTop + viewport.height`), and `padding-top: offsetTop` so
 *      content still renders at the visible area top. Keeping the top edge
 *      stationary means the container resizes only from the bottom as the
 *      keyboard opens/closes — no midpoint squeeze.
 *   2. Overrides `--size-h-screen` and `--size-h-screen-dynamic` to the visual
 *      viewport height so the page layout shrinks above the keyboard.
 *   3. Sets `--keyboard-offset` for portaled fixed-bottom elements (BottomBar,
 *      PopoverFooter) that live in <body> outside #app_container. These use
 *      normal `position: fixed` relative to the ICB, so they need an explicit
 *      offset to position above the keyboard.
 *   4. Blocks `touchmove` on the document level while the keyboard is open,
 *      except inside elements that are legitimate scroll containers.
 *
 * Why #app_container and not <body>? Portaled elements (BottomBar, PopoverFooter)
 * are direct children of <body>. Setting `position: fixed; overflow: hidden` on
 * <body> causes iOS Safari to treat its fixed children as absolute-positioned,
 * breaking their layout. By fixing #app_container instead, portaled elements in
 * <body> retain normal fixed positioning.
 *
 * Keyboard detection uses focus state: if an input / textarea / select /
 * contenteditable element is focused, we treat the keyboard as open.
 * `visualViewport` events still drive per-frame geometry (height, padding-top,
 * --keyboard-offset) — focus just tells us *when* the keyboard state changes,
 * avoiding the pixel-gap heuristic that also fires on Safari URL-bar collapse
 * and introduces delays at both ends of the keyboard animation.
 *
 * Call this once near the app root (e.g., AppFrame).
 */
export default function useVirtualKeyboardResize() {
    useEffect(() => {
        const viewport = window.visualViewport
        if (!viewport) {
            return
        }

        // Only needed on iOS — Android/Chrome handle this natively via
        // interactive-widget=resizes-content in the viewport meta tag.
        const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
        if (!isIOS) {
            return
        }

        const root = document.documentElement
        const appContainer = document.getElementById("app_container")
        if (!appContainer) {
            return
        }

        let rafId: number | null = null
        let isKeyboardOpen = false
        let isInputFocused = false
        let lastSetHeight: number | null = null

        const isInputLike = (element: EventTarget | null): element is HTMLElement => {
            if (!(element instanceof HTMLElement)) {
                return false
            }
            return (
                element.tagName === "INPUT" ||
                element.tagName === "TEXTAREA" ||
                element.tagName === "SELECT" ||
                element.isContentEditable
            )
        }

        // Focus drives the keyboard-open state, and we also preempt Safari's
        // focus-scroll: placing the input into view inside its scrollable
        // ancestor first means Safari usually has no reason to shift the
        // visual viewport (which would otherwise bounce against our tracking).
        const handleFocusIn = (event: FocusEvent) => {
            if (!isInputLike(event.target)) {
                return
            }
            isInputFocused = true
            event.target.scrollIntoView({ block: "nearest" })
            scheduleUpdate()
        }

        const handleFocusOut = (event: FocusEvent) => {
            if (!isInputLike(event.target)) {
                return
            }
            // If focus is moving to another input, stay in keyboard-open mode
            // to avoid a close/re-open flash.
            if (isInputLike(event.relatedTarget)) {
                return
            }
            isInputFocused = false
            scheduleUpdate()
        }

        // Prevents document-level scrolling while the keyboard is open, but
        // allows touch scrolling inside elements that have overflow: auto/scroll
        // and actually have scrollable content.
        const handleTouchMove = (event: TouchEvent) => {
            let target = event.target as HTMLElement | null
            while (target && target !== document.body && target !== root) {
                const { overflowY } = window.getComputedStyle(target)
                if ((overflowY === "auto" || overflowY === "scroll") && target.scrollHeight > target.clientHeight) {
                    return
                }
                target = target.parentElement
            }
            event.preventDefault()
        }

        const applyKeyboardOpen = () => {
            const viewportHeight = `${viewport.height}px`

            // CSS variables for layout sizing (consumed by .base, main, .page)
            root.style.setProperty("--size-h-screen", viewportHeight)
            root.style.setProperty("--size-h-screen-dynamic", viewportHeight)
            lastSetHeight = viewport.height

            // Fix #app_container pinned to the layout viewport top. `height`,
            // `padding-top`, and `--keyboard-offset` are tracked per-frame in
            // update() so TopBar / BottomBar / dialogs stay aligned with the
            // visible area as Safari shifts the visual viewport.
            appContainer.style.position = "fixed"
            appContainer.style.top = "0"
            appContainer.style.left = "0"
            appContainer.style.right = "0"
            appContainer.style.overflow = "hidden"

            // Smooth the 1-frame lag between Safari's visual-viewport animation
            // and our per-frame updates into a perceptual tween. Matches iOS's
            // typical 200-250ms keyboard animation. Doesn't remove the lag —
            // just masks the jerk as a soft settle.
            appContainer.style.transition = "height 0.15s ease-out, padding-top 0.15s ease-out"

            // Prevent scroll artifacts behind the fixed container
            root.style.overflow = "hidden"
        }

        const removeKeyboardOpen = () => {
            root.style.removeProperty("--size-h-screen")
            root.style.removeProperty("--size-h-screen-dynamic")
            root.style.removeProperty("--keyboard-offset")
            root.style.removeProperty("overflow")

            appContainer.style.removeProperty("position")
            appContainer.style.removeProperty("top")
            appContainer.style.removeProperty("left")
            appContainer.style.removeProperty("right")
            appContainer.style.removeProperty("height")
            appContainer.style.removeProperty("padding-top")
            appContainer.style.removeProperty("overflow")
            appContainer.style.removeProperty("transition")

            document.removeEventListener("touchmove", handleTouchMove)
            isKeyboardOpen = false
            lastSetHeight = null
        }

        const update = () => {
            rafId = null

            if (isInputFocused) {
                // Track the visible area every frame: container bottom edge
                // follows visible bottom, padding-top shims content to visible
                // top, and --keyboard-offset keeps BottomBar above the keyboard.
                const offsetTop = viewport.offsetTop
                appContainer.style.height = `${offsetTop + viewport.height}px`
                appContainer.style.paddingTop = `${offsetTop}px`

                const keyboardOffset = root.clientHeight - offsetTop - viewport.height
                root.style.setProperty("--keyboard-offset", `${Math.max(0, keyboardOffset)}px`)

                // Only re-apply CSS variables when keyboard height actually
                // changes. Safari fires viewport events when switching fields —
                // re-setting variables triggers layout recalcs that cause jumping.
                const heightChanged = lastSetHeight === null || Math.abs(viewport.height - lastSetHeight) > 10

                if (!isKeyboardOpen) {
                    applyKeyboardOpen()
                    document.addEventListener("touchmove", handleTouchMove, { passive: false })
                    isKeyboardOpen = true
                } else if (heightChanged) {
                    applyKeyboardOpen()
                }
            } else if (isKeyboardOpen) {
                removeKeyboardOpen()
            }
        }

        const scheduleUpdate = () => {
            if (rafId === null) {
                rafId = requestAnimationFrame(update)
            }
        }

        viewport.addEventListener("resize", scheduleUpdate)
        viewport.addEventListener("scroll", scheduleUpdate)
        document.addEventListener("focusin", handleFocusIn)
        document.addEventListener("focusout", handleFocusOut)

        return () => {
            viewport.removeEventListener("resize", scheduleUpdate)
            viewport.removeEventListener("scroll", scheduleUpdate)
            document.removeEventListener("focusin", handleFocusIn)
            document.removeEventListener("focusout", handleFocusOut)
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }
            if (isKeyboardOpen) {
                removeKeyboardOpen()
            }
        }
    }, [])
}
